/**
 * Buffer polyfill MUST be the very first thing executed.
 * Some Solana/Anchor dependencies access Buffer at module load time,
 * so it must exist on window before any other imports are evaluated.
 * The vite-plugin-node-polyfills also injects Buffer globally, but
 * this serves as a defensive fallback.
 */
import { Buffer } from 'buffer'
if (typeof window !== 'undefined' && !window.Buffer) {
  window.Buffer = Buffer
}

import React, { useMemo, useCallback } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react'
import { WalletAdapterNetwork, WalletError } from '@solana/wallet-adapter-base'
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui'
import { clusterApiUrl } from '@solana/web3.js'
import { Toaster, toast } from 'react-hot-toast'
import {
  SolanaMobileWalletAdapter,
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
  createDefaultWalletNotFoundHandler,
} from '@solana-mobile/wallet-adapter-mobile'

import App from './App'
import './styles/globals.css'
import debugLogger from './lib/debugLogger'
import ErrorBoundary from './components/ErrorBoundary'
import { TermsProvider } from './components/TermsProvider'

// Import required CSS for wallet adapter UI components
import '@solana/wallet-adapter-react-ui/styles.css'

/**
 * Main application entry point that sets up the React app with Solana wallet providers.
 * Configures wallet adapters for connecting to Phantom, Solflare and other wallets,
 * including mobile wallets via the Solana Mobile Wallet Adapter (MWA) protocol.
 */

// Configure the Solana network (can be 'mainnet-beta', 'testnet', or 'devnet')
const network = WalletAdapterNetwork.Devnet

// Get the RPC endpoint for the selected network
// You can replace this with a custom RPC endpoint for better performance
const endpoint = clusterApiUrl(network)

/**
 * Detects whether the current browser is running on a mobile device.
 * Used to conditionally configure mobile wallet adapter support.
 * Checks user agent string, touch capability, and screen width for comprehensive detection.
 */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }
  const userAgent = navigator.userAgent || navigator.vendor || ''
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  return mobileRegex.test(userAgent) || (hasTouchScreen && window.innerWidth < 768)
}

/**
 * Checks whether the current page is being served over a secure context (HTTPS).
 * MWA (Mobile Wallet Adapter) protocol REQUIRES HTTPS — deep links will silently fail
 * if the site is served over plain HTTP. This is because the MWA protocol uses
 * the WebCrypto API and secure WebSocket connections that require a secure origin.
 */
function isSecureContext(): boolean {
  if (typeof window === 'undefined') return false
  // window.isSecureContext covers HTTPS, localhost (treated as secure), and file:// in some browsers
  return window.isSecureContext === true
}

/**
 * Returns the canonical URI for the app, used for mobile wallet adapter app identity.
 * On mobile, the wallet app uses this URI to:
 *   1. Verify the dApp's identity (shown in the wallet's authorization prompt)
 *   2. Redirect back to the dApp after signing transactions
 *
 * Priority:
 *   1. VITE_APP_URL env variable (set in .env for production deployments)
 *   2. Current window location (works on HTTPS production deployments)
 *   3. Hardcoded fallback for the canonical production domain
 *
 * IMPORTANT: This URI MUST be an HTTPS URL for MWA to work.
 * Using http://localhost will cause the mobile wallet to fail silently.
 */
function getAppUrl(): string {
  // First priority: explicit env variable (recommended for production)
  // @ts-ignore — Vite injects env vars at build time; TS doesn't know about them without vite-env.d.ts
  const envUrl = import.meta.env.VITE_APP_URL
  if (envUrl && typeof envUrl === 'string' && envUrl.startsWith('https://')) {
    return envUrl
  }

  // Second priority: current window location (only if HTTPS)
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return `${window.location.protocol}//${window.location.host}`
  }

  // Fallback: canonical production URL
  // MUST be updated to match your actual deployed domain
  return 'https://ctopools.com'
}

/**
 * Custom toast configuration for better user experience.
 * Shows notifications for wallet connections, transactions, errors, etc.
 */
const toastOptions = {
  duration: 4000,
  position: 'bottom-right' as const,
  style: {
    background: '#0c101c',
    color: '#ffffff',
    border: '1px solid #00E0FF',
    borderRadius: '0.375rem',
    boxShadow: '0 0 10px rgba(0, 224, 255, 0.3)'
  },
  success: {
    iconTheme: {
      primary: '#00FF88',
      secondary: '#0c101c'
    }
  },
  error: {
    iconTheme: {
      primary: '#FF0080',
      secondary: '#0c101c'
    }
  }
}

/**
 * Main App Component with Providers.
 * Wraps the entire application with necessary context providers for:
 * - Solana connection management (ConnectionProvider)
 * - Wallet adapter functionality (WalletProvider) — includes mobile wallet adapter
 * - Wallet modal UI (WalletModalProvider)
 * - Terms of Service gate (TermsProvider)
 * - React routing (BrowserRouter)
 * - Toast notifications (Toaster)
 */
function MainApp() {
  debugLogger.info('MainApp', '🌟 Starting application initialization')
  debugLogger.timeStart('MainApp', 'initialization')

  // Use different basename for development vs production (GitHub Pages)
  const basename = process.env.NODE_ENV === 'production' ? '/cto-pools' : '/'
  debugLogger.info('MainApp', '🔍 Using basename', { basename, nodeEnv: process.env.NODE_ENV })

  const mobile = isMobileDevice()
  const secure = isSecureContext()
  const appUrl = getAppUrl()

  // Log mobile/HTTPS status for debugging
  debugLogger.info('MainApp', '📱 Environment check', {
    isMobile: mobile,
    isSecure: secure,
    appUrl,
    currentProtocol: typeof window !== 'undefined' ? window.location.protocol : 'N/A',
    currentHost: typeof window !== 'undefined' ? window.location.host : 'N/A',
    // @ts-ignore — Vite env var access
    envAppUrl: import.meta.env.VITE_APP_URL || '(not set)',
  })

  // Warn if on mobile but not HTTPS — MWA will NOT work
  if (mobile && !secure) {
    debugLogger.error(
      'MainApp',
      '⚠️ HTTPS REQUIRED: Mobile Wallet Adapter (MWA) requires a secure context (HTTPS). ' +
      'Deep links to mobile wallets will fail on HTTP. ' +
      'Deploy to an HTTPS domain or use a tunneling service (e.g., ngrok) for testing.'
    )
    console.warn(
      '[CTO Pools] ⚠️ Mobile wallet connections require HTTPS. ' +
      'The site is currently served over HTTP — wallet deep links will not work. ' +
      'Please access the site via HTTPS.'
    )
  }

  /**
   * Wallet adapters configuration.
   *
   * Desktop wallets (Phantom, Solflare, Backpack, etc.) implement the
   * Wallet Standard and are automatically detected by WalletProvider —
   * no manual adapter is needed for them.
   *
   * Mobile wallets use the Solana Mobile Wallet Adapter (MWA) protocol,
   * which requires explicit adapter registration. The SolanaMobileWalletAdapter
   * handles deep link URL schemes (solana-wallet://) and the MWA communication
   * protocol for transaction signing on mobile devices.
   *
   * The adapter is memoized to prevent re-instantiation on every render.
   */
  const wallets = useMemo(() => {
    const adapters = []

    // Add Mobile Wallet Adapter for mobile device support
    // This adapter enables deep-link based wallet connections on mobile browsers
    // It handles the MWA protocol: opening wallet apps, signing, and returning to the dApp
    if (mobile) {
      debugLogger.info('MainApp', '📱 Mobile device detected — registering MWA adapter')
      debugLogger.info('MainApp', '📱 MWA config', {
        appUrl,
        cluster: network,
        isSecure: secure,
      })

      try {
        const mwaAdapter = new SolanaMobileWalletAdapter({
          // Selects which address to use when the wallet authorizes multiple accounts
          addressSelector: createDefaultAddressSelector(),
          // Identifies this dApp to the mobile wallet (shown in wallet's authorization prompt)
          // The uri MUST be an HTTPS URL for the wallet to verify the dApp identity
          // and redirect back after authorization
          appIdentity: {
            name: 'CTO Pools',
            uri: appUrl,
            // Icon path relative to the uri — the wallet resolves this as {uri}/favicon.svg
            icon: 'favicon.svg',
          },
          // Caches authorization tokens so the user doesn't have to re-approve on every visit
          // Tokens are stored in localStorage and reused across sessions
          authorizationResultCache: createDefaultAuthorizationResultCache(),
          // Must match the network the RPC endpoint connects to
          // This tells the wallet which Solana cluster to use for transaction signing
          cluster: network,
          // When no compatible wallet app is installed, this handler shows an install prompt
          // directing the user to download a supported wallet (e.g., Phantom, Solflare)
          onWalletNotFound: createDefaultWalletNotFoundHandler(),
        })

        debugLogger.info('MainApp', '✅ MWA adapter created successfully', {
          adapterName: mwaAdapter.name,
          adapterUrl: mwaAdapter.url,
        })

        adapters.push(mwaAdapter)
      } catch (error) {
        debugLogger.error('MainApp', '❌ Failed to create MWA adapter', error)
        console.error('[CTO Pools] Failed to initialize mobile wallet adapter:', error)
      }
    } else {
      debugLogger.info('MainApp', '🖥️ Desktop detected — relying on Wallet Standard auto-detection')
    }

    return adapters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Global wallet error handler.
   * Catches errors from all wallet adapters (including MWA) and displays
   * user-friendly toast notifications so connection failures aren't silent.
   *
   * Common MWA errors:
   * - Secure context required (not HTTPS)
   * - Session timeout (wallet app took too long to respond)
   * - Authorization failed (user denied in wallet app)
   * - Wallet not found (no compatible wallet installed)
   */
  const onWalletError = useCallback((error: WalletError) => {
    const errorMessage = error.message || 'Unknown wallet error'
    const errorName = error.name || 'WalletError'

    debugLogger.error('MainApp', '💳 Wallet error caught', {
      name: errorName,
      message: errorMessage,
      error,
    })

    // Provide user-friendly error messages based on error type
    if (errorMessage.includes('secure context') || errorMessage.includes('HTTPS')) {
      toast.error(
        '🔒 HTTPS Required — Mobile wallet connections require a secure (HTTPS) connection. ' +
        'Please access the site via HTTPS.',
        { duration: 6000 }
      )
    } else if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
      toast.error(
        '⏱️ Connection Timed Out — The wallet app did not respond in time. ' +
        'Please try again and approve the connection promptly in your wallet app.',
        { duration: 5000 }
      )
    } else if (errorMessage.includes('not found') || errorMessage.includes('NotFound')) {
      toast.error(
        '📱 No Wallet Found — No compatible Solana wallet app was found on your device. ' +
        'Please install Phantom, Solflare, or another Solana wallet.',
        { duration: 6000 }
      )
    } else if (errorMessage.includes('denied') || errorMessage.includes('rejected') || errorMessage.includes('declined')) {
      toast.error(
        '❌ Connection Declined — The wallet connection was declined. ' +
        'Please approve the connection in your wallet app to continue.',
        { duration: 4000 }
      )
    } else if (errorName === 'WalletNotReadyError') {
      toast.error(
        '📱 Wallet Not Ready — The selected wallet is not ready. ' +
        'Make sure the wallet app is installed and up to date.',
        { duration: 5000 }
      )
    } else {
      // Generic fallback for unexpected errors
      toast.error(
        `Wallet Error: ${errorMessage}`,
        { duration: 4000 }
      )
    }
  }, [])

  debugLogger.info('MainApp', '🔗 Initializing Solana providers', {
    endpoint,
    walletCount: wallets.length,
    network,
    isMobile: mobile,
  })

  try {
    debugLogger.info('MainApp', '✅ Rendering provider tree')
    debugLogger.timeEnd('MainApp', 'initialization')

    return (
      <ErrorBoundary>
        <ConnectionProvider endpoint={endpoint}>
          <WalletProvider wallets={wallets} autoConnect onError={onWalletError}>
            <WalletModalProvider>
              {/* TermsProvider intercepts wallet connections and prompts users to accept Terms of Service */}
              <TermsProvider>
                <BrowserRouter basename={basename}>
                  <App />
                  <Toaster
                    toastOptions={toastOptions}
                    containerStyle={{
                      top: 20,
                      left: 20,
                      bottom: 20,
                      right: 20,
                    }}
                  />
                </BrowserRouter>
              </TermsProvider>
            </WalletModalProvider>
          </WalletProvider>
        </ConnectionProvider>
      </ErrorBoundary>
    )
  } catch (error) {
    debugLogger.error('MainApp', '❌ Critical error in provider setup', error)
    debugLogger.lifecycle('MainApp', 'error', { error })

    // Minimal fallback UI when providers fail to initialize
    return (
      <div className="min-h-screen bg-gray-900 text-white p-8">
        <h1 className="text-2xl mb-4">Application Error</h1>
        <p>Failed to initialize application providers.</p>
        <pre className="text-sm bg-gray-800 p-4 mt-4 rounded">
          {error instanceof Error ? error.message : String(error)}
        </pre>
      </div>
    )
  }
}

/**
 * Initialize and render the React application.
 * Ensures the DOM is ready before mounting the app.
 */
debugLogger.info('main.tsx', '🏁 Starting React application')
debugLogger.timeStart('main.tsx', 'app-render')

const rootElement = document.getElementById('root')
debugLogger.debug('main.tsx', '🔍 Root element check', { found: !!rootElement })

if (!rootElement) {
  debugLogger.error('main.tsx', '❌ Root element not found')
  throw new Error('Failed to find the root element. Make sure there is a div with id="root" in your HTML.')
}

debugLogger.debug('main.tsx', '🔍 Creating React root')
const root = ReactDOM.createRoot(rootElement)

debugLogger.debug('main.tsx', '🔍 Rendering React app')

// Add global error handlers before rendering
window.addEventListener('error', (event) => {
  debugLogger.error('main.tsx', '🚨 Global Error Event', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error
  })
})

window.addEventListener('unhandledrejection', (event) => {
  debugLogger.error('main.tsx', '🚨 Unhandled Promise Rejection', {
    reason: event.reason
  })
})

try {
  // Enable React Strict Mode for development warnings and better debugging
  root.render(
    <React.StrictMode>
      <MainApp />
    </React.StrictMode>
  )
  debugLogger.timeEnd('main.tsx', 'app-render')
  debugLogger.info('main.tsx', '✅ React app rendered successfully')
} catch (renderError) {
  debugLogger.error('main.tsx', '❌ Failed to render React app', renderError)
  debugLogger.lifecycle('main.tsx', 'error', { renderError })
}

// Hot reload support for development
// @ts-ignore — Vite HMR types not always recognized by TS
if (import.meta.hot) {
  // @ts-ignore — Vite HMR types
  import.meta.hot.accept()
}

debugLogger.info('main.tsx', '✅ Application bootstrap complete')
